import {
  ArchiveBoxXMarkIcon,
  ArrowUturnDownIcon,
  CheckIcon,
  EyeIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { GlobalConfigType, setLoanData } from 'actions'
import { MedianPriceLink } from 'components/MedianPriceLink'
import {
  type InputType,
  FieldOrigin,
  IS_NEXYS_COMPANY,
  IS_PRODUCTION,
  IVisibleProp,
  MINVALUE,
  MultiPropertyFields,
  propertySummaryFields,
  sandBoxNotEditableFields,
} from 'config'
import { convertNullToBlank } from 'pages/Resources/GuidelineOverlay/logic'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { CFPBRuralCheckAPI, getClosingCostEstimate, getGeoLocation, openS3Document } from 'services'
import { combinedMonthlyExpense } from 'services/pdfs/utils'
import { Button } from 'stories/components'
import { Tooltip } from 'stories/components/Tooltip/Tooltip'
import {
  areArraysEqual,
  getPrice2decimal,
  getPrice3decimal,
  isEmpty,
  onSplitText,
  openAuditLog,
  removeComma,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'

import type { ILoan, ILoanDetail, ILoanProcess, IPropertyChangedJson } from './interfaces'
import {
  analyzeLoanStructureValue,
  calculateBPiece,
  calculateLoanAmount,
  calculateLoanAmountFromLTC,
  calculateLTC,
  calculateLTV,
  estimatedClosingAmountBorrower,
  isLtvAdjustments,
  isNonDSCRProduct,
} from './Logic'
import { MultiPropertyInputs } from './MultiPropertyInputs'

interface IProps {
  loan_number: any
  loan: ILoan
  loanDetail: ILoanDetail
  loanProcessData: ILoanProcess
  showingFields: string[][]
  visibleFields: string[]
  propertyVisibleFields: string[][]
  loanFields: Record<string, InputType>
  multiPropertiesFields: Record<string, InputType>[]
  isAdmin: boolean | undefined
  brokerCanNotEditDetail: boolean
  sandBoxMode: boolean
  canEditLoanDetail: boolean
  exceptionApproved: boolean
  canPriceAdjust: boolean
  isBorrower?: boolean
  isPool: boolean
  isMultipleProperties: boolean
  anytimeCanEditLoanDetail: boolean | undefined
  changedLoanDetailJson: any
  changedPropertyDetailJson: IPropertyChangedJson[]
  originLoanDetail: ILoan | undefined
  maximumTotalLoanAmountTip: any
  priceAdjustmentObject: any
  logTable?: string
  showMap?: boolean
  onChangeLoanInputFields: (key: string, e: any) => void
  onBlur: (key: string) => void
  onAddProperty: () => void
  onChangePropertyFields: (index: number, key: string, e: any) => void
  onBlurProperty: (index: number, key: string) => void
  onRemoveProperty: (index: number) => void
}

export const LoanDetailInputs = (props: IProps) => {
  const {
    loanFields,
    multiPropertiesFields,
    loan,
    loanDetail,
    loanProcessData,
    showingFields,
    visibleFields,
    propertyVisibleFields,
    originLoanDetail,
    loan_number,
    isAdmin,
    brokerCanNotEditDetail,
    changedLoanDetailJson,
    changedPropertyDetailJson,
    maximumTotalLoanAmountTip,
    priceAdjustmentObject,
    sandBoxMode,
    canEditLoanDetail,
    exceptionApproved,
    anytimeCanEditLoanDetail,
    canPriceAdjust,
    isBorrower = false,
    isPool,
    isMultipleProperties,
    logTable = 'Loan',
    showMap = true,
    onChangeLoanInputFields,
    onBlur,
    onBlurProperty,
    onAddProperty,
    onChangePropertyFields,
    onRemoveProperty,
  } = props

  const [latLong, setLatLong] = useState({ lat: 0, lng: 0 })
  const [action, setAction] = useState('')
  const [LTV, setLTV] = useState<number>()
  const [LTC, setLTC] = useState<number>()
  const [cceUpdateTry, setCCEUpdateTry] = useState(0)
  const [loanAmountFillTry, setLoanAmountFillTry] = useState(0)
  const [tbdUpdateTry, setTBDUpdateTry] = useState(0)

  const isNewLoan = loan_number === 'New'
  const dispatch = useDispatch()

  const {
    minTotalConstructionFinanced,
    maxTotalConstructionFinanced,
    minConstructionFinancedHoldback,
    maxConstructionFinancedHoldback,
  } = loanDetail.loanGlobalSettings

  const { fieldProperties } = useSelector((state: any) => ({
    fieldProperties: ((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})[FieldOrigin.LoanStructure] ||
      {}) as Record<string, IVisibleProp>,
  }))

  useEffect(() => {
    const geoLocation = loan.geoLocation
    if (geoLocation && geoLocation.lat && geoLocation.lng) {
      setLatLong(geoLocation)
    } else {
      if (!isNewLoan)
        getGeoLocation().then((res) => {
          dispatch(setLoanData({ key: 'geoLocation', data: res }))
          setLatLong(res)
        })
    }
  }, [])

  useEffect(() => {
    if (cceUpdateTry > 0) onBlur('closingCostEstimate')
  }, [cceUpdateTry])

  useEffect(() => {
    if (loanAmountFillTry > 0) onBlur('proposedLoanAmount')
  }, [loanAmountFillTry])

  useEffect(() => {
    if (tbdUpdateTry > 0) onBlur('subjectPropertyAddressTBD')
  }, [tbdUpdateTry])

  useEffect(() => {
    if (Number(LTV) - Number(calculateLTV(loan)) !== 0) setLTV(calculateLTV(loan))
  }, [loan, LTV])

  useEffect(() => {
    if (Number(LTC) - Number(calculateLTC(loan)) !== 0) setLTC(calculateLTC(loan))
  }, [loan, LTC])

  const disableLoanDetailField = (key: string) => {
    if (Number(loanDetail.rateData?.price) > 0 && isBorrower) return true
    if (key === 'estimatedCreditScore' && loanDetail.submitToLoanSetup === 0) return false

    if (sandBoxMode) {
      if (sandBoxNotEditableFields.indexOf(key) !== -1) return true
      return false
    } else {
      if (exceptionApproved && !anytimeCanEditLoanDetail) return true
      if (canEditLoanDetail) return false
      return true
    }
  }

  const onShowHistory = async (key: string, table: string = logTable) => {
    const options = {
      table,
      field: loanFields[key].title,
      fieldLabel: loanFields[key].title,
      keys: {
        field: key,
        loanNumber: loanDetail.loanNumber,
      },
    }
    openAuditLog(options)
  }

  const CFPBRuralChecker = async () => {
    setAction('rural')
    const { fileKey } = await CFPBRuralCheckAPI()
    setAction('')
    openS3Document(fileKey, true)
  }

  const pullCCEFromFeeWorksheet = async (key: string) => {
    if (IS_PRODUCTION && !IS_NEXYS_COMPANY) return // Enable to Pull Closing Cost Estimate
    const closingCostEstimate = await getClosingCostEstimate()
    if (removeComma(loan.closingCostEstimate) != removeComma(closingCostEstimate)) {
      await onChangeLoanInputFields(key, closingCostEstimate)
      setCCEUpdateTry((prev) => prev + 1)
    }
  }

  const fillLoanAmountMax = async () => {
    await onChangeLoanInputFields('proposedLoanAmount', maximumTotalLoanAmountTip.minValue)
    setLoanAmountFillTry((prev) => prev + 1)
  }

  const onChangeTBD = async () => {
    await onChangeLoanInputFields('subjectPropertyAddressTBD', !loan.subjectPropertyAddressTBD)
    setTBDUpdateTry((prev) => prev + 1)
  }

  return (
    <div>
      {showingFields.map((fields, index) => {
        const isLtvAdjustment = isLtvAdjustments(fields)
        const showLtvAdjustments = isLtvAdjustment && !isNewLoan && isAdmin
        const hideLtvAdjustments = isLtvAdjustment && !(!isNewLoan && isAdmin)
        const isSummaryOfProperty = isMultipleProperties && areArraysEqual(propertySummaryFields, fields)
        const hasAPiece = fields.includes('aPieceAmount')
        if (hideLtvAdjustments) return <></>

        return (
          <div
            key={index}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 mb-5 rounded border border-solid border-gray-200 shadow1 p-2 justify-around"
          >
            {showLtvAdjustments && (
              <div className="col-span-2 text-[16px] font-variation-settings-600">Max LTV Adjustments</div>
            )}
            {fields.map((key, index1) => {
              if (!visibleFields.includes(key)) return <></>
              const hideError = isMultipleProperties && !isSummaryOfProperty && propertySummaryFields.includes(key)
              let rlt = []
              let cn = loanFields[key]?.length === 1 ? 'md:col-span-2' : 'md:col-span-1'
              let tooltip = fieldProperties[key] && fieldProperties[key].tooltip ? fieldProperties[key].tooltip : ''
              let additionalElements = null
              let disabled = false
              if (!loanFields[key]) {
                console.log('Unknown Field', key)
                return null
              }
              let readOnly = loanFields[key].readOnly
              if (isSummaryOfProperty) readOnly = true

              if (brokerCanNotEditDetail || exceptionApproved) {
                if (changedLoanDetailJson[key] !== undefined) {
                  cn += ' ring-2 ring-red-400 rounded-t'
                  const changeString = `${(originLoanDetail as any)[key]} -> ${(loan as any)[key]}`
                  if (tooltip) tooltip = `${tooltip}<br/>${changeString}`
                  else tooltip = changeString
                }
              }
              if (key === 'subjectPropertyAddress') {
                additionalElements = (
                  <div className="flex justify-center gap-2">
                    <MedianPriceLink address={loan.subjectPropertyAddress} />
                    <div className="flex items-center gap-0.5 hover:cursor-pointer" onClick={onChangeTBD}>
                      {!!loan.subjectPropertyAddressTBD ? (
                        <CheckIcon className="w-4 h-4 text-shade-blue" />
                      ) : (
                        <XMarkIcon className="w-4 h-4 text-red-700" />
                      )}
                      <span className="text-[12px]">TBD</span>
                    </div>
                  </div>
                )
                loanFields[key].required = !loan.subjectPropertyAddressTBD
              }
              if (key === 'closingCostEstimate' && !isNewLoan)
                additionalElements = (
                  <div className="flex gap-2 items-center font-variation-settings-600 text-shade-blue">
                    {(!IS_PRODUCTION || IS_NEXYS_COMPANY) && ( // Enable to Pull Closing Cost Estimate
                      <Tooltip message="Pull the correct value from Fee Worksheet.">
                        <ArrowUturnDownIcon
                          className="hover:cursor-pointer w-3.5 h-3.5"
                          onClick={() => pullCCEFromFeeWorksheet(key)}
                        />
                      </Tooltip>
                    )}
                    <Link to={`/fee_worksheet/${loanDetail.loanNumber}`} target="_blank">
                      <Tooltip message="Check Fee Worksheet">
                        <EyeIcon className="w-3.5 h-3.5 hover:cursor-pointer" />
                      </Tooltip>
                    </Link>
                  </div>
                )
              if (!priceAdjustmentObject[key] && key === 'constructionReserve' && minTotalConstructionFinanced === 100)
                readOnly = true
              if (key === 'proposedLoanAmount') {
                if (isNonDSCRProduct(loan.productType)) {
                  if (
                    maximumTotalLoanAmountTip.minValue !== MINVALUE &&
                    removeComma(maximumTotalLoanAmountTip.minValue) !== removeComma(loan[key])
                  ) {
                    additionalElements = (
                      <span className={`text-shade-blue hover:underline cursor-pointer`}>
                        <Tooltip
                          message={`<div>Max Amount: ${getPrice3decimal(maximumTotalLoanAmountTip.minValue)}</div>`}
                        >
                          <span className="font-variation-settings-600 px-3" onClick={fillLoanAmountMax}>
                            Fill Max
                          </span>
                        </Tooltip>
                      </span>
                    )
                  }
                }
              }
              if (key === 'subjectPropertyMap') cn += ' h-[400px]'
              if (key === 'estimatedCreditScore' && loan.isNoScore) disabled = true
              if (key === 'estimatedDscr') {
              } else if (key === 'subjectPropertyMap') {
                if (showMap && latLong.lat && latLong.lng)
                  rlt.push(
                    <div key={`${index}-${index1}`} className={cn}>
                      <RenderInput
                        input={{
                          ...loanFields[key],
                          value: latLong as any,
                        }}
                        Key={key}
                        onChange={onChangeLoanInputFields}
                        showHistory={onShowHistory}
                        onBlur={onBlur}
                      />
                    </div>,
                  )
              } else {
                if (isBorrower && isEmpty((loan as any)[key])) {
                } else
                  rlt.push(
                    <div key={`${index}-${index1}`} className={cn}>
                      <RenderInput
                        input={{
                          ...loanFields[key],
                          error: hideError
                            ? ''
                            : priceAdjustmentObject[key] !== undefined && (loan as any)[key] !== ''
                            ? ''
                            : loanFields[key].error,
                          value: isSummaryOfProperty
                            ? (analyzeLoanStructureValue(loan) as any)[key]
                            : (loan as any)[key],
                          history: !isNewLoan && !isSummaryOfProperty && !isPool,
                          disabled:
                            (disableLoanDetailField(key) || showLtvAdjustments ? !canPriceAdjust : false) || disabled,
                          tooltip: tooltip ? onSplitText(tooltip, 70) : tooltip,
                          additionalElements: isBorrower ? null : additionalElements,
                          readOnly,
                        }}
                        Key={key}
                        onChange={onChangeLoanInputFields}
                        showHistory={onShowHistory}
                        onBlur={onBlur}
                      />
                    </div>,
                  )
              }
              if (loan_number != 'New' && key === 'ruralProperty' && !isBorrower) {
                rlt.push(
                  <Button link className="w-fit" loading={action == 'rural'} onClick={CFPBRuralChecker}>
                    CFPB Rural Checker
                  </Button>,
                )
              }
              if (key === 'constructionReserve') {
                const { rehabFinancedPercent, totalRehabFinancedPercent, totalRehabFinancedTooltip } =
                  estimatedClosingAmountBorrower(loan, loanDetail.loanGlobalSettings, isSummaryOfProperty)

                const constructionFinancedHoldbackError =
                  removeComma(rehabFinancedPercent) > removeComma(maxConstructionFinancedHoldback) ||
                  removeComma(rehabFinancedPercent) < removeComma(minConstructionFinancedHoldback)
                    ? minConstructionFinancedHoldback === maxConstructionFinancedHoldback
                      ? `This may not be less than ${minConstructionFinancedHoldback}% of the Construction Budget`
                      : `Must be between ${minConstructionFinancedHoldback}% and ${maxConstructionFinancedHoldback}%`
                    : ''
                const totalRehabFinancedPercentError =
                  removeComma(totalRehabFinancedPercent) > removeComma(maxTotalConstructionFinanced) ||
                  removeComma(totalRehabFinancedPercent) < removeComma(minTotalConstructionFinanced)
                    ? maxTotalConstructionFinanced === minTotalConstructionFinanced
                      ? `This may not be less than ${minTotalConstructionFinanced}% of the Construction Budget`
                      : `Must be between ${minTotalConstructionFinanced}% and ${maxTotalConstructionFinanced}%`
                    : ''

                rlt.push(
                  <div key={`rehabFinancedPercent-${index}-${index1}`}>
                    <RenderInput
                      input={{
                        title: 'Construction Financed Holdback %',
                        inputType: 'text',
                        prefix: '%',
                        value: rehabFinancedPercent,
                        readOnly: true,
                        error: hideError
                          ? ''
                          : !isEmpty(priceAdjustmentObject['constructionFinancedHoldback'])
                          ? ''
                          : constructionFinancedHoldbackError,
                        tooltip: `'Construction Amount Financed'/<br/>'Construction Budget'`,
                      }}
                      Key={'rehabFinancedPercent'}
                      onChange={onChangeLoanInputFields}
                    />
                  </div>,
                )
                rlt.push(
                  <div key={`totalRehabFinancedPercent-${index}-${index1}`}>
                    <RenderInput
                      input={{
                        title: 'Total Construction Financed %',
                        inputType: 'text',
                        prefix: '%',
                        value: totalRehabFinancedPercent,
                        readOnly: true,
                        tooltip: totalRehabFinancedTooltip,
                        error: hideError
                          ? ''
                          : !isEmpty(priceAdjustmentObject['totalRehabFinancedPercent']) &&
                            !isEmpty(totalRehabFinancedPercent)
                          ? ''
                          : totalRehabFinancedPercentError,
                      }}
                      Key={'totalRehabFinancedPercent'}
                      onChange={onChangeLoanInputFields}
                    />
                  </div>,
                )
              }

              if (hasAPiece) {
                if (key === 'aPieceAmount') {
                  let aPieceTooltip = ''
                  let aPieceCN = 'md:col-span-1'
                  if (brokerCanNotEditDetail || exceptionApproved) {
                    if (!!originLoanDetail && changedLoanDetailJson['cltv'] !== undefined) {
                      aPieceCN += ' ring-2 ring-red-400 rounded-t'
                      aPieceTooltip = `${calculateBPiece(originLoanDetail)} -> ${calculateBPiece(loan)}`
                    }
                  }
                  rlt.push(
                    <div key={`aPieceAmount-${index}-${index1}`} className={aPieceCN}>
                      <RenderInput
                        input={{
                          title: 'B Piece',
                          inputType: 'text',
                          type: 'thousandSepNoDecimal',
                          prefix: '$',
                          value: calculateBPiece(loan),
                          tooltip: aPieceTooltip,
                          readOnly: true,
                        }}
                        Key={'bPieceAmount'}
                        onChange={onChangeLoanInputFields}
                      />
                    </div>,
                  )

                  let ltvTooltip = ''
                  let ltvCN = 'md:col-span-1'
                  if (brokerCanNotEditDetail || exceptionApproved) {
                    if (changedLoanDetailJson['ltv'] !== undefined) {
                      ltvCN += ' ring-2 ring-red-400 rounded-t'
                      ltvTooltip = `${calculateLTV(originLoanDetail)} -> ${calculateLTV(loan)}`
                    }
                  }
                  rlt.push(
                    <div key={`ltv-${index}-${index1}`} className={ltvCN}>
                      <RenderInput
                        input={{
                          title: 'LTV: A Piece',
                          inputType: 'text',
                          prefix: '%',
                          value: convertNullToBlank(LTV),
                          readOnly: disableLoanDetailField('LTV'),
                          tooltip: ltvTooltip,
                        }}
                        Key={'LTV'}
                        onChange={(_: string, value: any) => {
                          setLTV(value)
                          const loanAmount = calculateLoanAmount(loan, value)
                          onChangeLoanInputFields('aPieceAmount', loanAmount)
                        }}
                        onBlur={() => onBlur('aPieceAmount')}
                      />
                    </div>,
                  )

                  rlt.push(
                    <div key={`ltv-${index}-${index1}`} className="md:col-span-1">
                      <RenderInput
                        input={{
                          title: 'LTV: B Piece',
                          inputType: 'text',
                          prefix: '%',
                          value: convertNullToBlank(calculateLTV(loan, false, true)),
                          readOnly: true,
                        }}
                        Key={'LTV'}
                        onChange={() => {}}
                      />
                    </div>,
                  )

                  let cltvTooltip = ''
                  let cltvCN = 'md:col-span-1'
                  if (brokerCanNotEditDetail || exceptionApproved) {
                    if (changedLoanDetailJson['cltv'] !== undefined) {
                      cltvCN += ' ring-2 ring-red-400 rounded-t'
                      cltvTooltip = `${calculateLTV(originLoanDetail, true)} -> ${calculateLTV(loan, true)}`
                    }
                  }
                  rlt.push(
                    <div key={`cltv-${index}-${index1}`} className={cltvCN}>
                      <RenderInput
                        input={{
                          title: 'CLTV',
                          inputType: 'text',
                          prefix: '%',
                          value: convertNullToBlank(calculateLTV(loan, true)),
                          readOnly: true,
                          tooltip: cltvTooltip,
                        }}
                        Key={'CLTV'}
                        onChange={onChangeLoanInputFields}
                      />
                    </div>,
                  )

                  if (isNonDSCRProduct(loan.productType)) {
                    let ltcTooltip = ''
                    let ltcCN = 'md:col-span-1'
                    if (brokerCanNotEditDetail || exceptionApproved) {
                      if (changedLoanDetailJson['ltc'] !== undefined) {
                        ltcCN += ' ring-2 ring-red-400 rounded-t'
                        ltcTooltip = `${calculateLTC(originLoanDetail)} -> ${calculateLTC(loan)}`
                      }
                    }
                    rlt.push(
                      <div key={`ltc-${index}-${index1}`} className={ltcCN}>
                        <RenderInput
                          input={{
                            title: 'LTC',
                            inputType: 'text',
                            prefix: '%',
                            value: convertNullToBlank(LTC),
                            readOnly: disableLoanDetailField('LTC'),
                            tooltip: ltcTooltip,
                          }}
                          Key={'LTC'}
                          onChange={(_: string, value: any) => {
                            setLTC(value)
                            const loanAmount = calculateLoanAmountFromLTC(loan, value)
                            onChangeLoanInputFields('proposedLoanAmount', loanAmount)
                          }}
                          onBlur={() => onBlur('proposedLoanAmount')}
                        />
                      </div>,
                    )
                  }
                }
              } else {
                if (key === 'proposedLoanAmount') {
                  let ltvTooltip = ''
                  let ltvCN = 'md:col-span-1'
                  if (brokerCanNotEditDetail || exceptionApproved) {
                    if (changedLoanDetailJson['ltv'] !== undefined) {
                      ltvCN += ' ring-2 ring-red-400 rounded-t'
                      ltvTooltip = `${calculateLTV(originLoanDetail)} -> ${calculateLTV(loan)}`
                    }
                  }
                  rlt.push(
                    <div key={`ltv-${index}-${index1}`} className={ltvCN}>
                      <RenderInput
                        input={{
                          title: 'LTV',
                          inputType: 'text',
                          prefix: '%',
                          value: convertNullToBlank(LTV),
                          readOnly: disableLoanDetailField('LTV'),
                          tooltip: ltvTooltip,
                        }}
                        Key={'LTV'}
                        onChange={(_: string, value: any) => {
                          setLTV(value)
                          const loanAmount = calculateLoanAmount(loan, value)
                          onChangeLoanInputFields('proposedLoanAmount', loanAmount)
                        }}
                        onBlur={() => onBlur('proposedLoanAmount')}
                      />
                    </div>,
                  )

                  let cltvTooltip = ''
                  let cltvCN = 'md:col-span-1'
                  if (brokerCanNotEditDetail || exceptionApproved) {
                    if (changedLoanDetailJson['cltv'] !== undefined) {
                      cltvCN += ' ring-2 ring-red-400 rounded-t'
                      cltvTooltip = `${calculateLTV(originLoanDetail, true)} -> ${calculateLTV(loan, true)}`
                    }
                  }
                  rlt.push(
                    <div key={`cltv-${index}-${index1}`} className={cltvCN}>
                      <RenderInput
                        input={{
                          title: 'CLTV',
                          inputType: 'text',
                          prefix: '%',
                          value: convertNullToBlank(calculateLTV(loan, true)),
                          readOnly: true,
                          tooltip: cltvTooltip,
                        }}
                        Key={'CLTV'}
                        onChange={onChangeLoanInputFields}
                      />
                    </div>,
                  )

                  if (isNonDSCRProduct(loan.productType)) {
                    let ltcTooltip = ''
                    let ltcCN = 'md:col-span-1'
                    if (brokerCanNotEditDetail || exceptionApproved) {
                      if (changedLoanDetailJson['ltc'] !== undefined) {
                        ltcCN += ' ring-2 ring-red-400 rounded-t'
                        ltcTooltip = `${calculateLTC(originLoanDetail)} -> ${calculateLTC(loan)}`
                      }
                    }
                    rlt.push(
                      <div key={`ltc-${index}-${index1}`} className={ltcCN}>
                        <RenderInput
                          input={{
                            title: 'LTC',
                            inputType: 'text',
                            prefix: '%',
                            value: convertNullToBlank(LTC),
                            readOnly: disableLoanDetailField('LTC'),
                            tooltip: ltcTooltip,
                          }}
                          Key={'LTC'}
                          onChange={(_: string, value: any) => {
                            setLTC(value)
                            const loanAmount = calculateLoanAmountFromLTC(loan, value)
                            onChangeLoanInputFields('proposedLoanAmount', loanAmount)
                          }}
                          onBlur={() => onBlur('proposedLoanAmount')}
                        />
                      </div>,
                    )
                  }
                }
              }
              if (key === 'closingCostEstimate') {
                let possibleConstructionReserveMin = -1
                // let additionalElements = null
                let {
                  label,
                  value,
                  valueWithoutConstructionCalc,
                  closingTooltip,
                  closingMaxRehabBudget,
                  closingError,
                  constructionReserve,
                } = estimatedClosingAmountBorrower(loan, loanDetail.loanGlobalSettings)
                if (loan.transactionType === 'Refinance' && visibleFields.includes('rehabBudget')) {
                  if (maximumTotalLoanAmountTip.constructionReserveMinwithoutChangeLoanAmountValidation !== -1) {
                    possibleConstructionReserveMin = Math.max(
                      maximumTotalLoanAmountTip.constructionReserveMinwithoutChangeLoanAmountValidation,
                      valueWithoutConstructionCalc - closingMaxRehabBudget,
                    )
                    if (possibleConstructionReserveMin > removeComma(constructionReserve))
                      possibleConstructionReserveMin = -1
                  }

                  if (value > closingMaxRehabBudget)
                    possibleConstructionReserveMin = valueWithoutConstructionCalc - closingMaxRehabBudget
                }

                rlt.push(
                  <div key={`atClosingBorrowerAmount-${index}-${index1}`} className={cn}>
                    <RenderInput
                      input={{
                        title: label,
                        inputType: 'text',
                        prefix: '$',
                        value: getPrice2decimal(Math.abs(value)),
                        readOnly: true,
                        tooltip: closingTooltip,
                        error:
                          !isEmpty(priceAdjustmentObject['atClosingBorrowerAmount']) && !isEmpty(value)
                            ? ''
                            : closingError,
                        // additionalElements: additionalElements,
                      }}
                      Key={'atClosingBorrowerAmount'}
                      onChange={onChangeLoanInputFields}
                    />
                  </div>,
                )
              }
              if (key === 'estimatedDscr') {
                const pitis = combinedMonthlyExpense(loan, loanProcessData)
                if (pitis['dscr'] !== null) {
                  let tooltip = `${pitis.proposedMonthlyRent} = (Rent) <br/>/<br/> ${pitis['sum']} = (Mortgage Payment:${pitis['mortgagePayment']} + Insurance + Taxes + HoaDues + <br/> Second Lien Payment)`
                  let classNames = 'md:col-span-1'
                  if (brokerCanNotEditDetail || exceptionApproved) {
                    if (changedLoanDetailJson['dscr'] !== undefined) {
                      classNames += ' ring-2 ring-red-400 rounded-t'
                      const changeString = `${changedLoanDetailJson['dscr'].origin} -> ${changedLoanDetailJson['dscr'].changed}`
                      if (tooltip) tooltip = `${tooltip}<br/><br/>${changeString}`
                      else tooltip = changeString
                    }
                  }
                  rlt.push(
                    <div key={`calculatedDSCR-${index}-${index1}`} className={classNames}>
                      <RenderInput
                        input={{
                          title: 'Calculated DSCR',
                          inputType: 'text',
                          prefix: '%',
                          tooltip,
                          value: pitis['dscr'],
                          readOnly: true,
                        }}
                        Key={'estimatedDscr'}
                        onChange={() => {}}
                      />
                    </div>,
                  )
                }
              }
              if (isMultipleProperties) {
                const lastField = MultiPropertyFields.slice()
                  .reverse()
                  .find((v) => visibleFields.includes(v))
                if (key === lastField && !isBorrower)
                  rlt.push(
                    <div className="relative col-span-2">
                      <div className="flex justify-between items-center">
                        <div className="text-[16px] font-variation-settings-600">Additional Properties</div>
                        <div
                          className="flex items-center gap-1 text-shade-blue hover:underline hover:cursor-pointer text-sm"
                          onClick={onAddProperty}
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>Add Property</span>
                        </div>
                      </div>
                      {!!loan.howManyProperties &&
                        loan.howManyProperties != (loan?.multipleProperties?.length || 0) + 1 && (
                          <div className="border border-red-500 bg-red-100 text-red-700 px-3 py-2 rounded my-3">
                            {loan.howManyProperties > (loan?.multipleProperties?.length || 0) + 1
                              ? `${
                                  loan.howManyProperties - (loan?.multipleProperties?.length || 0) - 1
                                } more properties are required.`
                              : `There are ${(loan?.multipleProperties?.length || 0) + 1} properties. It must be ${
                                  loan.howManyProperties
                                }.`}
                          </div>
                        )}
                      {!loan?.multipleProperties?.length || !multiPropertiesFields.length ? (
                        <div className="w-full flex justify-center items-center mt-3">
                          <div className="flex flex-col items-center gap-1">
                            <ArchiveBoxXMarkIcon className="w-8 h-8 text-gray-400" />
                            <span className="text-gray-400 text-sm">No Properties</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          {loan.multipleProperties.length == multiPropertiesFields.length &&
                            loan.multipleProperties.map((item, index) => (
                              <MultiPropertyInputs
                                key={index}
                                targetIndex={index}
                                loan={loan}
                                propertyData={item}
                                loanDetail={loanDetail}
                                loanFields={multiPropertiesFields[index]}
                                loanProcessData={loanProcessData}
                                visibleFields={propertyVisibleFields[index]}
                                canEditLoanDetail={canEditLoanDetail}
                                canPriceAdjust={canPriceAdjust}
                                exceptionApproved={exceptionApproved}
                                anytimeCanEditLoanDetail={anytimeCanEditLoanDetail}
                                changedPropertyDetailJson={changedPropertyDetailJson.filter((v) => v.id == item.id)}
                                brokerCanNotEditDetail={brokerCanNotEditDetail}
                                onBlur={(key: string) => onBlurProperty(index, key)}
                                onChangeInputFields={(key: string, e: any) => onChangePropertyFields(index, key, e)}
                                onRemove={() => onRemoveProperty(index)}
                                priceAdjustmentObject={priceAdjustmentObject}
                                sandBoxMode={sandBoxMode}
                              />
                            ))}
                        </>
                      )}
                    </div>,
                  )
              }
              return rlt
            })}
          </div>
        )
      })}
    </div>
  )
}
